import { ROLE_DESCRIPTION, ROLE_LABEL, ROLES, type Role } from "@/lib/roles"

export type ConsoleUserId = string & { readonly __brand: "ConsoleUserId" }
export type ServiceKeyId = string & { readonly __brand: "ServiceKeyId" }

export interface ConsoleUser {
  id: ConsoleUserId
  email: string
  name: string | null
  role: Role
  /** Shown as "Joined". The backend has no login history, so this is the honest date. */
  createdAt: string
  invitedAt: string | null
  acceptedAt: string | null
  isActive: boolean
}

/**
 * Only states the backend actually stores. There is no "never signed in": the backend does
 * not record logins, so that badge could only ever have been a guess.
 */
export type AccountStatus = "active" | "pending-invite" | "deactivated"

export function accountStatus(user: ConsoleUser): AccountStatus {
  // Pending invite wins over `!isActive` — an invitee is inactive by construction until
  // acceptance, and reading that as "Deactivated" would say someone turned them off.
  if (user.invitedAt !== null && user.acceptedAt === null) return "pending-invite"
  if (!user.isActive) return "deactivated"
  return "active"
}

export interface ServiceKey {
  id: ServiceKeyId
  name: string
  prefix: string
  createdAt: string
  createdBy: string
  lastUsedAt: string | null
  fromEnv: boolean
}

export interface RoleOption {
  value: Role
  label: string
  description: string
}

// Labels and descriptions come from lib/roles, so this screen, the sidebar and the backend
// all use the same word for the same role.
export const ROLE_OPTIONS: readonly RoleOption[] = ROLES.map((role) => ({
  value: role,
  label: ROLE_LABEL[role],
  description: ROLE_DESCRIPTION[role],
}))

export function labelOfRole(role: Role): string {
  return ROLE_LABEL[role]
}

/**
 * User list filters. Both are applied by the backend (GET /api/v1/users `role`, `activity`),
 * never locally. The backend defines `activity` on `accepted_at`: `signed-in` is an accepted
 * invite, `never` is everything else.
 */
export interface UserFilters {
  role?: Role
  activity?: "signed-in" | "never"
}
